import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { escalationService } from '../services/api';
import { Table, Pagination, StatusToggle, PageHeader, useToast, Card } from './common';
import { usePageTitle } from '../context/PageTitleContext';
import { useSidebar } from '../context/SidebarContext';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import usePageState from '../hooks/usePageState';
import { useAuth } from '../context/AuthContext';
import useDepartment from '../hooks/useDepartment';

const EscalationManager = () => {
  const navigate       = useNavigate();
  const toast          = useToast();
  const { user, getPagePermissions } = useAuth();
  const dept = useDepartment();
  // Equipment is a BioMedical master; IT rules have no equipment dimension.
  const usesEquipment = (user?.department_code || 'IT') === 'BM';
  const permissions = getPagePermissions('/admin/escalations');
  const { setPageTitle } = usePageTitle();
  const { isCollapsed }  = useSidebar();

  const [escalations,    setEscalations]    = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [pagination,     setPagination]     = useState({
    totalPages: 1, totalItems: 0, itemsPerPage: 30
  });
  const [regionOptions,  setRegionOptions]  = useState([]);
  const [categoryOptions,setCategoryOptions]= useState([]);
  const [equipmentOptions,setEquipmentOptions]= useState([]);

  const [filters, setFilters] = usePageState('escalationManager', {
    currentPage: 1,
    itemsPerPage: 30,
    searchTerm: '',
    regionFilter: '',
    categoryFilter: '',
    equipmentFilter: '',
  }, ['/admin/escalations']);
  const { currentPage, itemsPerPage, searchTerm, regionFilter, categoryFilter, equipmentFilter } = filters;
  const setCurrentPage    = (v) => setFilters({ currentPage: v });
  const setItemsPerPage   = (v) => setFilters({ itemsPerPage: v });
  const setSearchTerm     = (v) => setFilters({ searchTerm: v });
  const setRegionFilter   = (v) => setFilters({ regionFilter: v });
  const setCategoryFilter = (v) => setFilters({ categoryFilter: v });
  const setEquipmentFilter = (v) => setFilters({ equipmentFilter: v });

  useEffect(() => { setPageTitle('Escalation Rules'); }, [setPageTitle]);

  // Load filter dropdown options once
  useEffect(() => {
    escalationService.getRegionsDropdown()
      .then(res => setRegionOptions(res.data || []))
      .catch(() => {});
    escalationService.getCategoriesDropdown()
      .then(res => setCategoryOptions(res.data || []))
      .catch(() => {});
    if (usesEquipment) {
      escalationService.getEquipmentDropdown()
        .then(res => setEquipmentOptions(res.data || []))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usesEquipment]);

  useEffect(() => {
    fetchEscalations();
  }, [currentPage, itemsPerPage, regionFilter, categoryFilter, equipmentFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEscalations = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page: currentPage, limit: itemsPerPage };
      if (regionFilter)   params.region_id   = regionFilter;
      if (categoryFilter) params.category_id = categoryFilter;
      if (equipmentFilter) params.equipment_id = equipmentFilter;

      const response = await escalationService.getAll(params);
      setEscalations(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to fetch escalation rules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegionFilterChange = (e) => {
    setRegionFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newVal) => {
    setItemsPerPage(newVal);
    setCurrentPage(1);
  };

  const handleView = (row) => navigate(`/admin/escalations/view/${row.id}`);
  const handleEdit = (row) => navigate(`/admin/escalations/edit/${row.id}`);

  const handleAdd = () => navigate('/admin/escalations/create');

  const handleDelete = async (id) => {
    const confirmed = await toast.confirm('Are you sure you want to delete this escalation rule?');
    if (!confirmed) return;
    try {
      await escalationService.delete(id);
      toast.success('Escalation rule deleted successfully');
      fetchEscalations();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete escalation rule');
    }
  };

  const handleStatusToggle = async (row, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [row.id]: true }));
    try {
      await escalationService.updateStatus(row.id, newStatus);
      toast.success(`Escalation rule ${newStatus ? 'activated' : 'deactivated'} successfully`);
      await fetchEscalations();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [row.id]: false }));
    }
  };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader
          addButton={permissions.can_add ? { label: 'Add Escalation Rule', onClick: handleAdd, variant: 'primary' } : null}
          filters={{
            dropdowns: [
              {
                value: regionFilter,
                onChange: handleRegionFilterChange,
                options: regionOptions,
                placeholder: `All ${dept.regions}`,
                width: 'w-[180px]',
                searchable: true,
              },
              {
                value: categoryFilter,
                onChange: handleCategoryFilterChange,
                options: categoryOptions,
                placeholder: 'All Categories',
                width: 'w-[180px]',
                searchable: true,
              },
              ...(usesEquipment ? [{
                value: equipmentFilter,
                onChange: (v) => { setEquipmentFilter(v?.target ? v.target.value : v); setCurrentPage(1); },
                options: equipmentOptions,
                placeholder: 'All Equipment',
                width: 'w-[180px]',
                searchable: true,
              }] : []),
            ]
          }}
        />

        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded-md border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <Table
          columns={[
            {
              header: 'S.No',
              accessor: 'serial',
              width: '70px',
              render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
            },
            {
              header: 'Type',
              accessor: 'is_default',
              width: '100px',
              align: 'center',
              render: (value) => value ? (
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                  DEFAULT
                </span>
              ) : (
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                  Custom
                </span>
              )
            },
            {
              header: dept.regions,
              accessor: 'regions_summary',
              width: '220px',
              render: (value, row) => row.is_default ? (
                <span className="text-sm text-purple-600 italic">All {dept.regions} (Default)</span>
              ) : (
                <span className="text-sm text-gray-600" title={value}>
                  {value && value.length > 40 ? `${value.substring(0, 40)}...` : value}
                </span>
              )
            },
            {
              header: 'Categories',
              accessor: 'categories_summary',
              width: '220px',
              render: (value, row) => row.is_default ? (
                <span className="text-sm text-purple-600 italic">All Categories (Default)</span>
              ) : (
                <span className="text-sm text-gray-600" title={value}>
                  {value && value.length > 40 ? `${value.substring(0, 40)}...` : value}
                </span>
              )
            },
            ...(usesEquipment ? [{
              header: 'Equipment',
              accessor: 'equipment_summary',
              width: '220px',
              render: (value, row) => row.is_default ? (
                <span className="text-sm text-purple-600 italic">All Equipment (Default)</span>
              ) : (
                <span className="text-sm text-gray-600" title={value}>
                  {value && value.length > 40 ? `${value.substring(0, 40)}...` : value}
                </span>
              )
            }] : []),
            {
              header: 'Levels',
              accessor: 'levels_count',
              width: '100px',
              align: 'center',
              render: (value) => (
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                  {value} {Number(value) === 1 ? 'Level' : 'Levels'}
                </span>
              )
            },
            {
              header: 'Created',
              accessor: 'created_at',
              width: '120px',
              render: (value) => formatDateDDMMYYYY(value)
            },
            {
              header: 'Status',
              accessor: 'status',
              width: '100px',
              align: 'center',
              render: (value, row) => (
                <StatusToggle
                  status={value}
                  onChange={(newStatus) => handleStatusToggle(row, newStatus)}
                  loading={updatingStatus[row.id]}
                  disabled={!permissions.can_edit}
                />
              )
            },
            {
              header: 'Actions',
              accessor: 'actions',
              width: '120px',
              align: 'center',
              actions: [
                ...(permissions.can_edit ? [{ type: 'edit',   onClick: handleEdit }] : []),
                ...(permissions.can_delete ? [{ type: 'delete', onClick: (row) => handleDelete(row.id) }] : [])
              ]
            }
          ]}
          data={permissions.can_list ? escalations : []}
          onRowClick={permissions.can_view ? handleView : undefined}
          emptyMessage={!permissions.can_list ? 'Permission Denied - You don\'t have permission to view this data' : 'No escalation rules found'}
          loading={loading}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </Card>
    </div>
  );
};

export default EscalationManager;
